import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { MapPin, Navigation2, Camera, PenLine, Check, X, AlertCircle, Package } from 'lucide-react';

const API = '/api/delivery';

/**
 * DriverApp — mobile-first PWA for the delivery driver.
 *
 * Flow per stop:
 *   scheduled -> [On my way] -> en_route -> [Arrived] -> arrived
 *     -> capture photo(s) + signature -> [Complete delivery] -> delivered
 *
 * Background: geolocation watchPosition pings the server every ~20s while the
 * route is in_progress, powering the public customer tracking page.
 */
export default function DriverApp() {
  const [route, setRoute]       = useState(null);
  const [stops, setStops]       = useState([]);
  const [activeStop, setActive] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const watchIdRef              = useRef(null);
  const lastPingRef             = useRef(0);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/driver/today`);
      setRoute(data.route);
      setStops(data.stops || []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Live location → server pings while route is in_progress
  useEffect(() => {
    if (!route || (route.status !== 'in_progress' && route.status !== 'dispatched' && route.status !== 'optimized')) {
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      return;
    }
    if (!('geolocation' in navigator)) return;
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const now = Date.now();
        if (now - lastPingRef.current < 15000) return; // throttle to ~every 15s
        lastPingRef.current = now;
        axios.post(`${API}/routes/${route.id}/ping`, {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          heading: pos.coords.heading || null,
          speed: pos.coords.speed || null,
          battery: null,
        }).catch(() => { /* silent */ });
      },
      (err) => console.warn('[driver] geolocation error:', err.message),
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
    );
    return () => {
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, [route]);

  if (loading) return <Screen><Center>Loading…</Center></Screen>;
  if (error)   return <Screen><Center><AlertCircle /> {error}</Center></Screen>;
  if (!route)  return <Screen><Center>No route assigned for today.</Center></Screen>;

  if (activeStop) {
    return (
      <StopDetail
        stop={stops.find(s => s.id === activeStop) || null}
        route={route}
        onBack={() => setActive(null)}
        onChanged={async () => { await load(); setActive(null); }}
      />
    );
  }

  const remaining = stops.filter(s => s.status === 'scheduled' || s.status === 'en_route' || s.status === 'arrived');
  const done      = stops.filter(s => s.status === 'delivered' || s.status === 'failed' || s.status === 'skipped');

  return (
    <Screen>
      <Header>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, letterSpacing: 1, color: '#8B6F4E', textTransform: 'uppercase' }}>Today's route</div>
          <div style={{ fontSize: 20, fontWeight: 600, color: '#2b2b2b' }}>
            {new Date(route.route_date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
          </div>
        </div>
        <div style={{ textAlign: 'right', fontSize: 13, color: '#666' }}>
          {done.length} / {stops.length}
        </div>
      </Header>

      <div style={{ padding: '0 16px 120px' }}>
        {remaining.length === 0 && done.length > 0 && (
          <div style={{ padding: 32, textAlign: 'center', color: '#2b5a2b', background: '#f3f8f0', borderRadius: 12 }}>
            All deliveries complete. Nice work.
          </div>
        )}
        {remaining.map(s => (
          <StopCard key={s.id} stop={s} onTap={() => setActive(s.id)} />
        ))}
        {done.length > 0 && (
          <div style={{ marginTop: 24, fontSize: 12, color: '#999', textTransform: 'uppercase', letterSpacing: 1 }}>
            Completed
          </div>
        )}
        {done.map(s => (
          <StopCard key={s.id} stop={s} onTap={() => setActive(s.id)} dim />
        ))}
      </div>
    </Screen>
  );
}

// ============================================================
// Stop card (list view)
// ============================================================
function StopCard({ stop, onTap, dim = false }) {
  const addr = [stop.address_line1, stop.city].filter(Boolean).join(', ');
  return (
    <button
      onClick={onTap}
      style={{
        width: '100%', textAlign: 'left', marginTop: 12,
        padding: 16, background: '#fff', border: '1px solid #e5e5e0',
        borderRadius: 12, cursor: 'pointer',
        opacity: dim ? 0.55 : 1,
        display: 'flex', gap: 12, alignItems: 'flex-start',
      }}
    >
      <div style={{
        minWidth: 36, height: 36, borderRadius: '50%',
        background: '#8B6F4E', color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 15, fontWeight: 600,
      }}>{stop.sequence || '·'}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, color: '#2b2b2b', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {stop.customer_name}
          </span>
          <StatusPill status={stop.status} />
        </div>
        <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>{addr}</div>
        <div style={{ fontSize: 12, color: '#888', marginTop: 4, display: 'flex', gap: 8, alignItems: 'center' }}>
          {stop.box_count != null && (
            <span style={{
              background: '#8B6F4E', color: '#fff', padding: '2px 8px', borderRadius: 10,
              fontSize: 11, fontWeight: 700,
            }}>
              <Package size={11} style={{ verticalAlign: -1, marginRight: 3 }} />
              {stop.box_count} {stop.box_count === 1 ? 'box' : 'boxes'}
            </span>
          )}
          <span>{stop.order_number} · {stop.item_count || 0} items</span>
        </div>
      </div>
    </button>
  );
}

// ============================================================
// Stop detail + actions
// ============================================================
function StopDetail({ stop, route, onBack, onChanged }) {
  const [busy, setBusy]           = useState(false);
  const [showPod, setShowPod]     = useState(false);

  if (!stop) return <Screen><Center>Stop not found.</Center></Screen>;

  const fullAddr = [stop.address_line1, stop.address_line2, stop.city, stop.state, stop.zip].filter(Boolean).join(', ');
  const mapsUrl  = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(fullAddr)}&travelmode=driving`;

  const act = async (path, body) => {
    setBusy(true);
    try {
      await axios.post(`${API}/stops/${stop.id}${path}`, body || {});
      await onChanged();
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  };

  const fail = async () => {
    const reason = prompt('Reason for failed delivery?');
    if (!reason) return;
    await act('/failed', { reason });
  };

  if (showPod) {
    return (
      <ProofOfDelivery
        stop={stop}
        onCancel={() => setShowPod(false)}
        onSubmit={async (payload) => {
          setBusy(true);
          try {
            await axios.post(`${API}/stops/${stop.id}/delivered`, payload);
            await onChanged();
          } catch (err) {
            alert(err.response?.data?.error || err.message);
            setBusy(false);
          }
        }}
      />
    );
  }

  return (
    <Screen>
      <Header>
        <button onClick={onBack} style={iconBtn}><X size={22} /></button>
        <div style={{ flex: 1, marginLeft: 12 }}>
          <div style={{ fontSize: 11, letterSpacing: 1, color: '#8B6F4E' }}>STOP {stop.sequence || '·'}</div>
          <div style={{ fontSize: 17, fontWeight: 600, color: '#2b2b2b' }}>{stop.customer_name}</div>
        </div>
        <StatusPill status={stop.status} />
      </Header>

      <div style={{ padding: 16 }}>
        <div style={{ background: '#fff', border: '1px solid #e5e5e0', borderRadius: 12, padding: 16 }}>
          <div style={{ fontSize: 13, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>Deliver to</div>
          <div style={{ fontSize: 17, fontWeight: 600, marginTop: 4 }}>{stop.ship_to_name || stop.customer_name}</div>
          <div style={{ fontSize: 15, color: '#444', marginTop: 6, lineHeight: 1.4 }}>{fullAddr}</div>
          {stop.customer_phone && (
            <div style={{ fontSize: 14, marginTop: 10 }}>
              <a href={`tel:${stop.customer_phone}`} style={{ color: '#8B6F4E', textDecoration: 'none' }}>
                📞 {stop.customer_phone}
              </a>
            </div>
          )}
          <a
            href={mapsUrl} target="_blank" rel="noopener noreferrer"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              marginTop: 16, padding: '14px 16px', background: '#8B6F4E', color: '#fff',
              borderRadius: 10, textDecoration: 'none', fontWeight: 600,
            }}
          >
            <Navigation2 size={18} /> Navigate
          </a>
        </div>

        <div style={{ background: '#fff', border: '1px solid #e5e5e0', borderRadius: 12, padding: 16, marginTop: 12 }}>
          <div style={{ fontSize: 13, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>Order</div>
          <div style={{ fontSize: 15, marginTop: 4 }}>{stop.order_number}</div>
          {stop.box_count != null && (
            <div style={{
              marginTop: 10, padding: '10px 12px', borderRadius: 8,
              background: '#8B6F4E', color: '#fff', fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: 8, fontSize: 15,
            }}>
              <Package size={18} />
              Load {stop.box_count} {stop.box_count === 1 ? 'box' : 'boxes'}
              {stop.total_weight ? ` · ${stop.total_weight} lb` : ''}
            </div>
          )}
          <div style={{ fontSize: 13, color: '#666', marginTop: 6 }}>{stop.item_count || 0} items</div>
          {stop.delivery_notes && (
            <div style={{ marginTop: 10, padding: 10, background: '#fbf7f2', borderRadius: 8, fontSize: 13, color: '#6b5540' }}>
              <strong>Note:</strong> {stop.delivery_notes}
            </div>
          )}
        </div>

        {/* State-machine action buttons */}
        <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {stop.status === 'scheduled' && (
            <button
              disabled={busy}
              onClick={() => act('/en-route', { notifyCustomer: true })}
              style={bigPrimary}
            >
              On my way
            </button>
          )}
          {stop.status === 'en_route' && (
            <button disabled={busy} onClick={() => act('/arrived')} style={bigPrimary}>
              I've arrived
            </button>
          )}
          {stop.status === 'arrived' && (
            <button disabled={busy} onClick={() => setShowPod(true)} style={bigPrimary}>
              <Camera size={18} style={{ marginRight: 8, verticalAlign: -3 }} />
              Complete delivery (photo + signature)
            </button>
          )}
          {(stop.status === 'scheduled' || stop.status === 'en_route' || stop.status === 'arrived') && (
            <button disabled={busy} onClick={fail} style={bigDanger}>
              Couldn't deliver
            </button>
          )}
          {stop.status === 'delivered' && (
            <div style={{ padding: 16, textAlign: 'center', color: '#2b5a2b', background: '#f3f8f0', borderRadius: 10 }}>
              <Check size={18} style={{ verticalAlign: -3, marginRight: 6 }} />
              Delivered {stop.completed_at ? new Date(stop.completed_at).toLocaleTimeString() : ''}
            </div>
          )}
          {stop.status === 'failed' && (
            <div style={{ padding: 16, textAlign: 'center', color: '#991b1b', background: '#fee2e2', borderRadius: 10 }}>
              Failed: {stop.failure_reason || 'unspecified'}
            </div>
          )}
        </div>
      </div>
    </Screen>
  );
}

// ============================================================
// Proof of delivery: photo + signature capture
// ============================================================
function ProofOfDelivery({ stop, onCancel, onSubmit }) {
  const [photos, setPhotos] = useState([]);           // array of base64 strings
  const [signatureDataUrl, setSignatureDataUrl] = useState(null);
  const [signatureName, setSignatureName] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const handleFiles = (files) => {
    const next = [...photos];
    Array.from(files).forEach(f => {
      const reader = new FileReader();
      reader.onload = () => {
        next.push(reader.result);
        setPhotos([...next]);
      };
      reader.readAsDataURL(f);
    });
  };

  const submit = async () => {
    if (photos.length === 0 && !signatureDataUrl) {
      if (!window.confirm('No photo or signature captured. Mark delivered anyway?')) return;
    }
    setSubmitting(true);
    let pos = { lat: null, lng: null };
    try {
      const p = await new Promise((resolve) =>
        navigator.geolocation.getCurrentPosition(
          r => resolve({ lat: r.coords.latitude, lng: r.coords.longitude }),
          () => resolve({ lat: null, lng: null }),
          { maximumAge: 60000, timeout: 5000 }
        )
      );
      pos = p;
    } catch { /* ignore */ }
    await onSubmit({
      photos,
      signature_png: signatureDataUrl,
      signature_name: signatureName || null,
      recipient_note: note || null,
      lat: pos.lat, lng: pos.lng,
    });
  };

  return (
    <Screen>
      <Header>
        <button onClick={onCancel} style={iconBtn}><X size={22} /></button>
        <div style={{ flex: 1, marginLeft: 12, fontSize: 17, fontWeight: 600 }}>Proof of delivery</div>
      </Header>

      <div style={{ padding: 16 }}>
        <div style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
          {stop.customer_name} · {stop.order_number}
        </div>

        {/* Photos */}
        <Panel title={`Photos (${photos.length})`}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            {photos.map((src, i) => (
              <div key={i} style={{ position: 'relative' }}>
                <img src={src} alt="" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8 }} />
                <button
                  onClick={() => setPhotos(photos.filter((_, idx) => idx !== i))}
                  style={{
                    position: 'absolute', top: -6, right: -6,
                    width: 22, height: 22, borderRadius: '50%',
                    background: '#a03030', color: '#fff', border: 'none', cursor: 'pointer',
                    fontSize: 12, lineHeight: 1,
                  }}
                >×</button>
              </div>
            ))}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            onChange={e => handleFiles(e.target.files)}
            style={{ display: 'none' }}
          />
          <button onClick={() => fileInputRef.current?.click()} style={btnOutline}>
            <Camera size={16} style={{ marginRight: 8, verticalAlign: -3 }} />
            {photos.length ? 'Add another photo' : 'Take a photo'}
          </button>
        </Panel>

        {/* Signature */}
        <Panel title="Signature">
          <SignaturePad value={signatureDataUrl} onChange={setSignatureDataUrl} />
          <input
            type="text"
            placeholder="Printed name (optional)"
            value={signatureName}
            onChange={e => setSignatureName(e.target.value)}
            style={{ width: '100%', padding: 12, marginTop: 10, border: '1px solid #d8d4ca', borderRadius: 8, fontSize: 15, boxSizing: 'border-box' }}
          />
        </Panel>

        <Panel title="Note (optional)">
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            rows={3}
            placeholder="e.g. left at front desk"
            style={{ width: '100%', padding: 12, border: '1px solid #d8d4ca', borderRadius: 8, fontSize: 15, resize: 'vertical', boxSizing: 'border-box' }}
          />
        </Panel>

        <button
          onClick={submit}
          disabled={submitting}
          style={{ ...bigPrimary, marginTop: 16 }}
        >
          {submitting ? 'Saving…' : (
            <><Check size={18} style={{ marginRight: 8, verticalAlign: -3 }} /> Complete delivery</>
          )}
        </button>
      </div>
    </Screen>
  );
}

// ============================================================
// Signature pad (canvas)
// ============================================================
function SignaturePad({ value, onChange }) {
  const canvasRef = useRef(null);
  const drawing   = useRef(false);
  const last      = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const c = canvasRef.current;
    const ratio = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width  = rect.width * ratio;
    c.height = rect.height * ratio;
    const ctx = c.getContext('2d');
    ctx.scale(ratio, ratio);
    ctx.strokeStyle = '#2b2b2b';
    ctx.lineWidth   = 2;
    ctx.lineCap     = 'round';
    ctx.lineJoin    = 'round';
  }, []);

  const point = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    if (e.touches && e.touches[0]) {
      return { x: e.touches[0].clientX - r.left, y: e.touches[0].clientY - r.top };
    }
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    last.current = point(e);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const p = point(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    onChange(canvasRef.current.toDataURL('image/png'));
  };
  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, c.width, c.height);
    onChange(null);
  };

  return (
    <div>
      <div style={{ border: '1px dashed #d8d4ca', borderRadius: 8, background: '#fff', touchAction: 'none' }}>
        <canvas
          ref={canvasRef}
          style={{ width: '100%', height: 180, display: 'block', borderRadius: 8 }}
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
        <span style={{ fontSize: 12, color: '#999' }}>
          <PenLine size={12} style={{ verticalAlign: -1, marginRight: 4 }} />
          Sign above
        </span>
        <button onClick={clear} style={{ background: 'transparent', border: 'none', color: '#8B6F4E', fontSize: 13, cursor: 'pointer' }}>
          Clear
        </button>
      </div>
    </div>
  );
}

// ============================================================
// Primitives
// ============================================================
function Screen({ children }) {
  return (
    <div style={{
      minHeight: '100vh', background: '#FAFAF8',
      fontFamily: '-apple-system, Segoe UI, Helvetica, Arial, sans-serif',
      color: '#2b2b2b',
    }}>{children}</div>
  );
}
function Header({ children }) {
  return (
    <div style={{
      position: 'sticky', top: 0, zIndex: 10,
      background: '#FAFAF8', padding: '16px',
      display: 'flex', alignItems: 'center', gap: 12,
      borderBottom: '1px solid #e5e5e0',
    }}>{children}</div>
  );
}
function Center({ children }) {
  return <div style={{ padding: 48, textAlign: 'center', color: '#666' }}>{children}</div>;
}
function Panel({ title, children }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e5e0', borderRadius: 12, padding: 16, marginTop: 12 }}>
      <div style={{ fontSize: 12, color: '#8B6F4E', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600, marginBottom: 10 }}>{title}</div>
      {children}
    </div>
  );
}
function StatusPill({ status }) {
  const map = {
    scheduled: { bg: '#f0eee8', fg: '#6b5540' },
    en_route:  { bg: '#fef3c7', fg: '#854d0e' },
    arrived:   { bg: '#dbeafe', fg: '#1e40af' },
    delivered: { bg: '#d1fae5', fg: '#065f46' },
    failed:    { bg: '#fee2e2', fg: '#991b1b' },
    skipped:   { bg: '#f3f4f6', fg: '#6b7280' },
  };
  const c = map[status] || map.scheduled;
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 10,
      fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
      background: c.bg, color: c.fg, marginLeft: 6,
    }}>{status?.replace('_', ' ')}</span>
  );
}

const iconBtn = {
  padding: 6, background: 'transparent', border: 'none', cursor: 'pointer', color: '#666',
};
const bigPrimary = {
  width: '100%', padding: 16, background: '#8B6F4E', color: '#fff',
  border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 600, cursor: 'pointer',
};
const bigDanger = {
  width: '100%', padding: 14, background: '#fff', color: '#a03030',
  border: '1px solid #e5c0c0', borderRadius: 12, fontSize: 15, fontWeight: 500, cursor: 'pointer',
};
const btnOutline = {
  padding: '12px 18px', background: '#fff', color: '#6b5540',
  border: '1px solid #d8d4ca', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer',
};
